import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import Page from '../../../components/Page';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { decryptString, getAssetURl } from '../../../utils/url';
import { getPropertySubCategory } from '../../../store/propertySubCategory/api';
import { PropertySubCategory } from '../../../store/propertySubCategory/api/interface';
import Loader from '../../../components/Loader';
import { MdNumbers, MdOutlineTitle } from 'react-icons/md';
import { GrStatusGoodSmall } from 'react-icons/gr';
import Label from '../../../components/Label';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BiSolidCategory } from 'react-icons/bi';
import CloseIcon from '@mui/icons-material/Close';
import { IoLogoIonic } from 'react-icons/io5';
import { isValidArray } from '../../../utils/valid';

type Params = {
  id: string;
};

export default function index() {
  const dispatch = useAppDispatch();
  const params = useParams<Params>();
  const navigation = useNavigate();
  const decodeParam = decryptString(params.id ?? '');
  const { isLoading } = useAppSelector((state) => state.propertySubCategory);
  const [propertySubCategory, setPropertySubCategory] =
    useState<PropertySubCategory>();

  useEffect(() => {
    const fetchById = async () => {
      try {
        const record = await dispatch(
          getPropertySubCategory({ id: decodeParam })
        ).unwrap();
        setPropertySubCategory(record);
      } catch (error: any) {
        navigation('/property-sub-category');
      }
    };
    fetchById();
  }, []);

  const handleBack = async () => {
    navigation(-1);
  };

  const boxRef = useRef<HTMLDivElement>(null);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [imageState, setImageState] = useState<string>();

  const handleOpenLightBox = (imageObject: string) => {
    setOpenLightBox(true);
    setImageState(imageObject);
  };

  const handleCloseLightBox = () => {
    setOpenLightBox(false);
    setImageState('');
  };

  return (
    <Page title={'View Property Sub Category'}>
      <Container maxWidth={'xl'}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
          spacing={{ xs: 0.5, sm: 1.5 }}
          my={1}
        >
          <Button
            onClick={handleBack}
            variant='outlined'
            startIcon={<ArrowBackIcon />}
          >
            {'Back'}
          </Button>
        </Stack>
        <Card>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <CardHeader title={'Sub Category Information'} />
              <Grid
                container
                spacing={2}
                mb={3}
                ml={1}
                mr={1}
                mt={1}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <MdOutlineTitle className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Label EN:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{propertySubCategory?.labelEn}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <MdOutlineTitle className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Label AR:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{propertySubCategory?.labelAr}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <BiSolidCategory className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Category:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{propertySubCategory?.property_category?.labelEn}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <MdNumbers className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Position:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{propertySubCategory?.position}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <GrStatusGoodSmall className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Status:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      <Label
                        variant='ghost'
                        color={
                          propertySubCategory?.activeState ? 'success' : 'error'
                        }
                      >
                        {propertySubCategory?.activeState
                          ? 'Active'
                          : 'InActive'}
                      </Label>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={12}
                >
                  <Stack
                    direction='row'
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <IoLogoIonic className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Icon:</Typography>
                    {propertySubCategory?.icon && (
                      <Avatar
                        onClick={() =>
                          handleOpenLightBox(propertySubCategory?.icon)
                        }
                        alt={propertySubCategory?.icon}
                        imgProps={{ crossOrigin: 'anonymous' }}
                        src={getAssetURl(propertySubCategory?.icon)}
                        sx={{
                          borderRadius: 'unset',
                          m: 1,
                          ml: 2,
                          objectFit: 'contain',
                          '& img': { objectFit: 'contain' },
                        }}
                      />
                    )}
                  </Stack>
                </Grid>
              </Grid>
              {isValidArray(
                propertySubCategory?.propertyFacilitySubCategoryMapper
              ) && (
                <>
                  <CardHeader title={'Property Facilities'} />
                  <Box
                    sx={{
                      whiteSpace: 'normal',
                      m: 2,
                      maxHeight: 150,
                      overflow: 'auto',
                    }}
                  >
                    {propertySubCategory?.propertyFacilitySubCategoryMapper?.map(
                      (facilitySubCategory) => (
                        <Chip
                          label={
                            facilitySubCategory?.property_facility?.labelEn
                          }
                          sx={{ m: 1 }}
                        />
                      )
                    )}
                  </Box>
                </>
              )}
              {isValidArray(
                propertySubCategory?.propertyFeatureSubCategoryMapper
              ) && (
                <>
                  <CardHeader title={'Property Features'} />
                  <Box
                    sx={{
                      whiteSpace: 'normal',
                      m: 2,
                      maxHeight: 150,
                      overflow: 'auto',
                    }}
                  >
                    {propertySubCategory?.propertyFeatureSubCategoryMapper?.map(
                      (featureSubCategory) => (
                        <Chip
                          label={featureSubCategory?.property_feature?.labelEn}
                          sx={{ m: 1 }}
                        />
                      )
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </Card>
      </Container>

      {openLightBox && (
        <Box
          sx={{ pt: 'env(safe-area-inset-top) !important' }}
          id='myModal'
          className='modal'
        >
          <span className='close cursor'>
            <CloseIcon
              fontSize='large'
              onClick={handleCloseLightBox}
              sx={{ mt: 'env(safe-area-inset-top) !important' }}
            />
          </span>
          <div
            className={`modal-content ${
              openLightBox ? 'display-block ' : 'display-none'
            }`}
          >
            {imageState && (
              <div className='vertical-center-div'>
                <div
                  ref={boxRef}
                  className={`mySlides ${
                    openLightBox ? 'display-block ' : 'display-none'
                  }`}
                >
                  <img
                    id={imageState}
                    src={getAssetURl(imageState)}
                    width='100%'
                    height='100%'
                    crossOrigin='anonymous'
                    alt={imageState}
                  />
                </div>
              </div>
            )}
          </div>
        </Box>
      )}
    </Page>
  );
}
